<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList, SbListItem, SbListLabel, SbListSection } from "@unio/saltbox-ui"
import { computed, ref, useTemplateRef } from "vue"

import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import FlashAlerts from "@/components/FlashAlerts.vue"
import LabeledSection from "@/components/LabeledSection.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import {
  downloadInventoryExportPath,
  importInventoryExportsPath,
  inventoryExportPath,
  inventoryExportsPath,
} from "@/routes"
import type { ExportEntry } from "@/types/models"

const props = defineProps<{
  exports: ExportEntry[]
}>()

const { title } = useNavbar({ title: "Import / Export" })

const exporting = ref(false)
const importing = ref(false)
const fileInput = useTemplateRef<HTMLInputElement>("fileInput")

const deleteTarget = ref<string | null>(null)
const deleteOpen = computed(() => deleteTarget.value !== null)
const deleting = ref(false)

function exportAll() {
  exporting.value = true
  router.post(
    inventoryExportsPath(),
    {},
    {
      onFinish: () => (exporting.value = false),
    },
  )
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  importing.value = true
  router.post(
    importInventoryExportsPath(),
    { file },
    {
      forceFormData: true,
      onFinish: () => {
        importing.value = false
        if (fileInput.value) fileInput.value.value = ""
      },
    },
  )
}

function confirmDelete() {
  if (!deleteTarget.value) return
  deleting.value = true
  router.delete(inventoryExportPath({ id: deleteTarget.value }), {
    onFinish: () => {
      deleting.value = false
      deleteTarget.value = null
    },
  })
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <FlashAlerts />

    <SbList bordered separator>
      <SbListItem>
        <SbListSection avatar>
          <UIcon :name="ICON.export" class="text-muted size-5" />
        </SbListSection>
        <SbListSection>
          <SbListLabel>Export all</SbListLabel>
          <SbListLabel caption>Export entire inventory as a ZIP file</SbListLabel>
        </SbListSection>
        <SbListSection side>
          <UButton
            label="Export"
            color="neutral"
            variant="subtle"
            size="sm"
            :loading="exporting"
            @click="exportAll"
          />
        </SbListSection>
      </SbListItem>

      <SbListItem>
        <SbListSection avatar>
          <UIcon :name="ICON.upload" class="text-muted size-5" />
        </SbListSection>
        <SbListSection>
          <SbListLabel>Import</SbListLabel>
          <SbListLabel caption
            >Import inventory from a ZIP (additive — nothing is deleted)</SbListLabel
          >
        </SbListSection>
        <SbListSection side>
          <UButton
            label="Choose file"
            color="neutral"
            variant="subtle"
            size="sm"
            :loading="importing"
            @click="fileInput?.click()"
          />
          <input
            ref="fileInput"
            type="file"
            accept=".zip"
            class="hidden"
            @change="onFileSelected"
          />
        </SbListSection>
      </SbListItem>
    </SbList>

    <LabeledSection
      v-if="props.exports.length"
      title="Previous exports"
      :count="props.exports.length"
    >
      <SbListItem v-for="exp in props.exports" :key="exp.name">
        <SbListSection avatar>
          <UIcon :name="ICON.archive" class="text-muted size-5" />
        </SbListSection>
        <SbListSection>
          <SbListLabel :lines="1">{{ exp.name }}</SbListLabel>
          <SbListLabel caption>{{ exp.size_human }} · {{ exp.modified_at_human }}</SbListLabel>
        </SbListSection>
        <SbListSection side>
          <div class="flex items-center gap-1">
            <UButton
              :icon="ICON.export"
              color="neutral"
              variant="ghost"
              size="xs"
              square
              :to="downloadInventoryExportPath({ id: exp.name })"
              external
            />
            <UButton
              :icon="ICON.delete"
              color="neutral"
              variant="ghost"
              size="xs"
              square
              @click="
                () => {
                  deleteTarget = exp.name
                }
              "
            />
          </div>
        </SbListSection>
      </SbListItem>
    </LabeledSection>

    <DeleteConfirmModal
      :open="deleteOpen"
      @update:open="!$event && (deleteTarget = null)"
      :title="`Delete ${deleteTarget}?`"
      :loading="deleting"
      @confirm="confirmDelete"
    >
      <p class="text-sm">This export file will be permanently deleted.</p>
    </DeleteConfirmModal>
  </NarrowPage>
</template>
